import gql from 'graphql-tag';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { Container } from '@components';
import { StickyNavFragment } from '@graphqlTypes';
import { useViewSize } from '@/providers/ViewSizeContext';
import { useScroll } from '@/providers/ScrollContext';
import { useHeader } from '@/providers/HeaderContext';
import { useRefContext } from '@/providers/RefContext';
import { useGSAP } from "@gsap/react";
import CustomEase from 'gsap/CustomEase';

import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

import className from 'classnames/bind';
import styles from './StickyNav.module.scss';
import { usePageWrapperData } from '@/providers/PageWrapperContext';
import { replaceStringVar } from '@/core/utils';
import { useClientMount } from '@/providers/ClientMountProvider';
const cx = className.bind(styles);

export default function StickyNav({
  items,
  showImmediately,
  onActiveSectionChange,
  centerActiveAtMid = false,
  fadeUntilActive = false,
}: StickyNavFragment & {
  onActiveSectionChange?: (sectionId: string | null) => void;
  centerActiveAtMid?: boolean;
  fadeUntilActive?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const trigger = useRef<ScrollTrigger | null>(null);
  const scrollProgress = useRef<number[]>([]);
  const scrollPositions = useRef<{ [key: string]: { start: number; end: number } }>({});
  const { viewWidth, isMobile, isTouchDevice, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const { labels: { ariaLabels } = {} } = usePageWrapperData();
  const { scrollY } = useScroll();
  const [isStuck, setIsStuck] = useState(false);
  const sectionRefs = useRef<{ [key: string]: HTMLLIElement | null }>({});
  const { headerVisible, headerRef } = useHeader();
  const { footerRef } = useRefContext();
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const [hasFadedIn, setHasFadedIn] = useState(false); // Track whether we've already faded in
  const router = useRouter();

  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
  });

  // -------------------- Fade Animation

  // Ensure initial opacity when fadeUntilActive is enabled, or enforce visible when disabled
  useEffect(() => {
    if (!mounted || !isReady || !contentRef.current) return;
    if (fadeUntilActive && !hasFadedIn) {
      gsap.set(contentRef.current, { opacity: 0 });
    } else {
      gsap.set(contentRef.current, { opacity: 1 });
    }
  }, [mounted, isReady, fadeUntilActive, hasFadedIn]);

  // When the first section becomes active, fade in (only once)
  useEffect(() => {
    if (!mounted || !isReady || !fadeUntilActive || hasFadedIn || !contentRef.current) return;
    if (activeSection) {
      gsap.to(contentRef.current, {
        opacity: 1,
        duration: 0.4,
        ease: 'power1.out',
        onComplete: () => setHasFadedIn(true),
      });
    }
  }, [mounted, isReady, activeSection, fadeUntilActive, hasFadedIn]);

  // --------------------

  // Set ref for each link
  const setLinkRef = (ref: HTMLLIElement | null, sectionId: string) => {
    if (!ref) return;
    sectionRefs.current[sectionId] = ref;
  }

  // Smooth scroll to section on link click
  const onLinkClick = (sectionId: string, e?: React.MouseEvent<HTMLAnchorElement>, instant = false) => {
    if (!sectionId || !sectionRefs.current[sectionId]) return;
    e?.preventDefault();
    const sectionElement = document.getElementById(sectionId);
    if (!sectionElement) return;
    const { top } = sectionElement.getBoundingClientRect();
    const stickyNavHeight = rootRef.current?.offsetHeight || 0;
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const offsetY = top < 0 ? stickyNavHeight + headerHeight : stickyNavHeight;
    let innerScrollY = instant ? window.scrollY : scrollY;

    gsap.to(window, {
      scrollTo: {
        y: innerScrollY + top + 2, // Scroll 2px past target to resolve issues with activeSection on Android. -Will
        offsetY
      },
      duration: instant ? 0.25 : 0.75,
      ease: 'power1.out',
      onComplete: () => {
        const heading = sectionElement.querySelector('h1, h2, h3, h4, h5, h6');
        if (heading) {
          (heading as HTMLElement).setAttribute('tabindex', '0'); // This is necessary in order to make the heading focusable. -Will
          (heading as HTMLElement).focus();
        }
      },
    });
  }

  // Initialize scroll positions for each section
  const initScrollProgress = () => {
    if (!trigger.current || !rootRef.current) return;
    items.forEach((item, index) => {
      const sectionTitle = document.getElementById(item.sectionId);
      const sectionEnd = document.querySelector<HTMLElement>(`.section-end[data-section-id="${item.sectionId}"]`);
      if (!sectionTitle || !sectionEnd) {
        console.groupCollapsed(`StickyNav: Section ${item.sectionId} not found`);
        console.warn(`Section Title:`, sectionTitle);
        console.warn(`Section End:`, sectionEnd);
        console.groupEnd();
        return;
      }
      scrollPositions.current[item.sectionId] = {
        start: sectionTitle.offsetTop,
        end: sectionEnd.offsetTop - rootRef.current.offsetHeight,
      };
    });
  }

  // Adjust position when stuck/unstuck or header visibility changes
  useEffect(() => {
    if (!mounted || !isReady) return;
    if (isStuck && headerVisible) {
      gsap.to(contentRef.current, {
        y: headerRef.current?.offsetHeight || 0,
        duration: 0.3,
        ease: 'easeOutQuad',
      })
    } else if (isStuck && !headerVisible) {
      gsap.to(contentRef.current, {
        y: 0,
        duration: 0.3,
        ease: 'easeOutQuad',
      })
    }
  }, [mounted, isReady, headerVisible, isStuck]);

  // Center active link in view if needed
  useEffect(() => {
    if (activeSection) {
      const activeLink = sectionRefs.current[activeSection];
      if (activeLink && activeLink.parentElement) {
        const container = activeLink.parentElement;
        const containerRect = container.getBoundingClientRect();
        const linkRect = activeLink.getBoundingClientRect();

        if (linkRect.left < containerRect.left || linkRect.right > containerRect.right) {
          const scrollLeft = activeLink.offsetLeft - (containerRect.width / 2) + (linkRect.width / 2);
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [activeSection]);

  // On mobile, if we're stuck and the header is visible, set the initial offset after a short delay
  useEffect(() => {
    if (!mounted || !isReady) return;
    if (isStuck && headerVisible) {
      gsap.delayedCall(0.25, () => {
        gsap.set(contentRef.current, {
          y: headerRef.current?.offsetHeight || 0,
        });
      });
    }
  }, [mounted, isReady, isMobile, isStuck, headerVisible]);

  // If we unstick and are scrolled back to the top, reset position
  useEffect(() => {
    if (!mounted || !isReady) return;
    if (!isStuck && trigger.current && scrollY <= trigger.current.start) {
      gsap.to(contentRef.current, {
        y: 0,
        duration: 0.3,
        ease: 'power1.out',
      });
    }
  }, [mounted, isReady, isStuck, scrollY]);

  //
  useEffect(() => {
    if (!trigger.current || !rootRef.current) return;
    const viewportMid = (window.innerHeight || document.documentElement.clientHeight) / 2;
    const centerY = scrollY + viewportMid;
    const checkY = centerActiveAtMid ? centerY : scrollY;

    const found = items.find(item => {
      const { start, end } = scrollPositions.current?.[item.sectionId] || {};
      return checkY >= start && checkY <= end;
    });
    const newActive = found ? found.sectionId : null;
    if (newActive !== activeSection) {
      setActiveSection(newActive);
      onActiveSectionChange?.(newActive);
    }
    items.forEach((item, index) => {
      if (!scrollPositions.current[item.sectionId]) return;
      const { start, end } = scrollPositions.current[item.sectionId];
      const progress = ((checkY - start) / (end - start)) * 100;
      scrollProgress.current[index] = Math.round(Math.max(0, Math.min(100, progress)) * 10) / 10;
    });
  }, [scrollY, items, activeSection, onActiveSectionChange]);

  // Recalculate scroll positions on view size change
  useEffect(() => {
    initScrollProgress();
  }, [viewWidth]);

  // Initialize ScrollTrigger for sticky behavior
  useEffect(() => {
    if (!mounted || !isReady || !rootRef.current || !footerRef) return;
    const lastSectionEnd = document.querySelector<HTMLElement>(`.section-end[data-section-id="${items[items.length - 1].sectionId}"]`);
    const ctx = gsap.context(() => {
      gsap.registerPlugin(CustomEase);
      CustomEase.create("easeOutQuad", "0.5, 1, 0.89, 1");
      trigger.current = ScrollTrigger.create({
        trigger: rootRef.current,
        endTrigger: lastSectionEnd || footerRef,
        start: 'top top',
        end: 'top top+=50',
        pinSpacing: false,
        onToggle: (self) => setIsStuck(self.isActive),
        pin: true,
      });
      setTimeout(() => {
        trigger.current.refresh();
        initScrollProgress();
      }, 350);
    }, rootRef);
    return () => ctx.revert();
  }, [mounted, isReady, rootRef, footerRef]);

  useEffect(() => {
    const section = router.asPath.split('#')[1];
    if (section) {
      setTimeout(() => { onLinkClick(section, null, true); }, 100);
    }
  }, []);

  // -------------------- Render

  return (
    <div className={cx(["sticky-nav", {'is-stuck': isStuck, 'is-touch': isTouchDevice, 'show-immediately': showImmediately}])} ref={rootRef}>
      <div className={cx(["sticky-nav-content"])} ref={contentRef}>
        <Container>
          <ul className={cx(["sticky-nav-list"])}>
            {items.map((item) => (
              <li
                key={item.sectionId}
                ref={ref => setLinkRef(ref, item.sectionId)}
                className={cx(["sticky-nav-item"])}
                style={{ '--scroll-progress': `${scrollProgress.current[items.indexOf(item)] || 0}%` } as React.CSSProperties}
              >
                <a
                  className={cx(["sticky-nav-link", "cta"])}
                  href={`#${item.sectionId}`}
                  onClick={(e) => onLinkClick(item.sectionId, e)}
                  role="link"
                  aria-label={replaceStringVar(ariaLabels?.jumpTo, { label: item.label })}
                >
                    {item.label}
                  </a>
              </li>
            ))}
          </ul>
        </Container>
      </div>
    </div>
  );

}

StickyNav.fragments = {
  entry: gql`
    fragment StickyNav on FlexiblePageContentContentStickyNavLayout {
      items {
        label
        sectionId
      }
      showImmediately
    }
  `,
};
